import React from 'react';
import { useState } from 'react';
import { Autocomplete, AutocompleteItem, Spinner } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { useFood } from '~/hooks/useFood';
import useDebouncedCallback from '~/hooks/useDebouncedCallback';
import { FoodResponseDto } from '@bitecritic-api/src/foods/dto/food.response.dto';

interface FoodAutocompleteProps {
  onChange: (food: FoodResponseDto | undefined) => void;
  value: number | undefined;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  description?: string;
  label?: string;
  placeholder?: string;
  initialFood?: FoodResponseDto | null;
}

export default function FoodAutocomplete({
  onChange,
  value,
  isRequired = false,
  isInvalid = false,
  errorMessage,
  description,
  label,
  placeholder,
  initialFood,
}: FoodAutocompleteProps) {
  const [input, setInput] = useState('');
  const [inputValue, setInputValue] = useState(initialFood?.name || '');
  const [isTouched, setIsTouched] = useState(false);
  const { t } = useTranslation();
  const { data, isLoading, error: fetchError } = useFood(input, initialFood || undefined, 50);
  const debouncedSetInput = useDebouncedCallback((value: string) => {
    setInput(value);
  }, 500);

  const showError = isInvalid || (isTouched && isRequired && !value);
  const validationError = showError
    ? errorMessage || (isRequired && !value ? t('validation.food-required') || 'Food selection is required' : undefined)
    : undefined;

  if (fetchError) {
    return <div className="text-danger">{t('common.error.default') || 'An error occurred'}</div>;
  }

  return (
    <Autocomplete
      label={label || t('what-did-you-eat') || 'What did you eat?'}
      placeholder={placeholder || t('select-food') || 'Select a food'}
      onSelectionChange={(id) => {
        const food = data?.items.find((f) => `${f.id}` === `${id}`);
        onChange(food);
        setIsTouched(true);
        if (food) {
          setInputValue(food.name);
        }
      }}
      selectedKey={value?.toString()}
      inputValue={inputValue}
      onInputChange={(value) => {
        setInputValue(value);
        debouncedSetInput(value);
        setIsTouched(true);
      }}
      onClose={() => setIsTouched(true)}
      onBlur={() => setIsTouched(true)}
      isRequired={isRequired}
      isInvalid={showError}
      errorMessage={validationError}
      description={description}
      endContent={isLoading && <Spinner size="sm" />}
      itemHeight={40}
      items={data?.items || []}
    >
      {(item) => (
        <AutocompleteItem
          key={item.id}
          textValue={item.name}
        >
          <div className="flex gap-2">
            <div className="flex flex-col">
              <span className="text-foreground font-semibold">{item.name}</span>
              {item.category && (
                <span className="text-sm text-foreground/50">{item.category.name}</span>
              )}
            </div>
          </div>
        </AutocompleteItem>
      )}
    </Autocomplete>
  );
}
